import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  RefreshCw, Search, Pause, Play, XCircle, Mail, Loader2,
  ChevronDown, ChevronUp, Send, Edit, Plus, Trash2
} from 'lucide-react';

const statusColors = {
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
  pending: 'bg-blue-100 text-blue-700',
  pending_cancel: 'bg-orange-100 text-orange-700',
  payment_failed: 'bg-red-100 text-red-700',
};

const AdminSubscriptions = () => {
  const { toast } = useToast();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  // Edit state
  const [editSub, setEditSub] = useState(null);
  const [editForm, setEditForm] = useState({
    items: [],
    shipping_address: '',
    recurrence_months: '4',
    next_delivery_date: '',
    discount_percent: '0',
  });
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => { fetchSubs(); }, []);

  const fetchSubs = async () => {
    try {
      const { data } = await api.get('/subscriptions');
      setSubs(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load subscriptions', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleAction = async (subId, action) => {
    setActionLoading(`${subId}-${action}`);
    try {
      await api.patch(`/subscriptions/${subId}/${action}`);
      toast({ title: 'Success', description: `Subscription ${action}d successfully` });
      fetchSubs();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || `Failed to ${action}`, variant: 'destructive' });
    } finally { setActionLoading(''); }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast({ title: 'Error', description: 'Subject and body are required', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      await api.post(`/subscriptions/${emailModal}/send-email`, {
        subject: emailSubject, body: emailBody
      });
      toast({ title: 'Sent', description: 'Email sent to customer' });
      setEmailModal(null);
      setEmailSubject('');
      setEmailBody('');
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to send email', variant: 'destructive' });
    } finally { setSending(false); }
  };

  const openEdit = (sub) => {
    const nextDel = sub.next_delivery_date || sub.next_delivery;
    let dateStr = '';
    if (nextDel) {
      try {
        const d = new Date(nextDel);
        dateStr = d.toISOString().split('T')[0];
      } catch { /* ignore */ }
    }
    setEditForm({
      items: (sub.items || []).map(i => ({
        size: i.size || '', merv: i.merv || 8, quantity: i.quantity || 1, price: i.price || 0
      })),
      shipping_address: sub.shipping_address || '',
      recurrence_months: String(sub.recurrence_months || 4),
      next_delivery_date: dateStr,
      discount_percent: String(sub.discount_percent ?? 0),
    });
    setEditSub(sub);
  };

  const handleEditItem = (idx, field, value) => {
    setEditForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const handleAddItem = () => {
    setEditForm(prev => ({
      ...prev,
      items: [...prev.items, { size: '', merv: 8, quantity: 1, price: 0 }]
    }));
  };

  const handleRemoveItem = (idx) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
  };

  const handleSaveEdit = async () => {
    if (!editSub) return;
    if (editForm.items.length === 0) {
      toast({ title: 'Error', description: 'At least one item is required', variant: 'destructive' });
      return;
    }
    setEditLoading(true);
    try {
      const payload = {
        items: editForm.items.map(i => ({
          size: i.size,
          merv: parseInt(i.merv),
          quantity: parseInt(i.quantity),
          price: parseFloat(i.price),
        })),
        shipping_address: editForm.shipping_address,
        recurrence_months: parseInt(editForm.recurrence_months),
        discount_percent: parseFloat(editForm.discount_percent),
      };
      if (editForm.next_delivery_date) {
        payload.next_delivery_date = new Date(editForm.next_delivery_date).toISOString();
      }
      await api.put(`/subscriptions/${editSub.id}`, payload);
      toast({ title: 'Updated', description: `Subscription ${editSub.id} updated` });
      setEditSub(null);
      fetchSubs();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to update', variant: 'destructive' });
    } finally { setEditLoading(false); }
  };

  const [processingDue, setProcessingDue] = useState(false);

  const handleProcessDue = async () => {
    setProcessingDue(true);
    try {
      const { data } = await api.post('/subscriptions/admin/process-due');
      toast({ title: 'Processed', description: data.message || `${data.orders_created} orders created, ${data.reminders_sent} reminders sent` });
      fetchSubs();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to process', variant: 'destructive' });
    } finally { setProcessingDue(false); }
  };

  const filtered = subs.filter(s => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(s.customer_name || '').toLowerCase().includes(q) &&
          !(s.customer_email || '').toLowerCase().includes(q) &&
          !(s.id || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  const counts = { all: subs.length, active: 0, paused: 0, cancelled: 0, payment_failed: 0 };
  subs.forEach(s => { if (counts[s.status] !== undefined) counts[s.status]++; });

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-subscriptions-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Subscription Management</h1>
            <p className="text-sm text-gray-500 mt-1">Manage all customer subscriptions</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleProcessDue} disabled={processingDue} data-testid="process-due-btn">
              {processingDue ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              {processingDue ? 'Processing...' : 'Process Due'}
            </Button>
            <Button variant="outline" onClick={fetchSubs} data-testid="refresh-subs-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total', count: counts.all, color: 'bg-slate-100 text-slate-700' },
            { label: 'Active', count: counts.active, color: 'bg-emerald-100 text-emerald-700' },
            { label: 'Paused', count: counts.paused, color: 'bg-amber-100 text-amber-700' },
            { label: 'Cancelled', count: counts.cancelled, color: 'bg-red-100 text-red-700' },
          ].map(s => (
            <Card key={s.label}>
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold" data-testid={`sub-count-${s.label.toLowerCase()}`}>{s.count}</p>
                <Badge className={`${s.color} mt-1`}>{s.label}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search by name, email, or ID" value={search}
                onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="sub-search-input" />
            </div>
          </div>
          <div className="flex gap-1">
            {['all', 'active', 'paused', 'cancelled'].map(s => (
              <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'}
                onClick={() => setStatusFilter(s)} data-testid={`sub-filter-${s}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Subscription List */}
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <RefreshCw className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No subscriptions found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(sub => (
              <Card key={sub.id} className="overflow-hidden" data-testid={`sub-card-${sub.id}`}>
                <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpanded(expanded === sub.id ? null : sub.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-gray-900 truncate">{sub.customer_name || 'Unknown'}</p>
                      <Badge className={statusColors[sub.status] || 'bg-gray-100 text-gray-700'}>
                        {sub.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{sub.customer_email || 'N/A'}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>Every {sub.recurrence_months || '?'}mo</span>
                    <span className="font-mono">{sub.id?.slice(0, 12)}</span>
                    {expanded === sub.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>

                {expanded === sub.id && (
                  <div className="px-5 pb-5 pt-0 border-t border-gray-100 bg-slate-50/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 text-sm">
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase mb-1">Items</p>
                        {(sub.items || []).map((item, i) => (
                          <p key={i} className="text-gray-700">{item.size} MERV {item.merv} x{item.quantity} @ ${item.price}</p>
                        ))}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase mb-1">Shipping</p>
                        <p className="text-gray-700">{sub.shipping_address || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase mb-1">Next Delivery</p>
                        <p className="text-gray-700">
                          {sub.next_delivery ? new Date(sub.next_delivery).toLocaleDateString() : 'N/A'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Discount: {sub.discount_percent ?? 0}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase mb-1">Details</p>
                        <p className="text-gray-700">Payment: {sub.payment_method || 'Stripe'}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Created: {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-gray-200">
                      {/* Edit button — always visible for non-cancelled */}
                      {sub.status !== 'cancelled' && (
                        <Button size="sm" variant="outline" className="text-sky-600 border-sky-200 hover:bg-sky-50"
                          onClick={(e) => { e.stopPropagation(); openEdit(sub); }}
                          data-testid={`edit-sub-${sub.id}`}>
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                      )}
                      {sub.status === 'active' && (
                        <>
                          <Button size="sm" variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleAction(sub.id, 'pause'); }}
                            disabled={actionLoading === `${sub.id}-pause`}
                            data-testid={`pause-sub-${sub.id}`}>
                            {actionLoading === `${sub.id}-pause` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Pause className="h-4 w-4 mr-1" />}
                            Pause
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={(e) => { e.stopPropagation(); handleAction(sub.id, 'cancel'); }}
                            disabled={actionLoading === `${sub.id}-cancel`}
                            data-testid={`cancel-sub-${sub.id}`}>
                            {actionLoading === `${sub.id}-cancel` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                            Cancel
                          </Button>
                        </>
                      )}
                      {sub.status === 'paused' && (
                        <Button size="sm" variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleAction(sub.id, 'resume'); }}
                          disabled={actionLoading === `${sub.id}-resume`}
                          data-testid={`resume-sub-${sub.id}`}>
                          {actionLoading === `${sub.id}-resume` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                          Resume
                        </Button>
                      )}
                      {(sub.status === 'cancelled' || sub.status === 'payment_failed') && (
                        <Button size="sm" variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleAction(sub.id, 'reactivate'); }}
                          disabled={actionLoading === `${sub.id}-reactivate`}
                          data-testid={`reactivate-sub-${sub.id}`}>
                          {actionLoading === `${sub.id}-reactivate` ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                          Reactivate
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={(e) => { e.stopPropagation(); setEmailModal(sub.id); setEmailSubject(''); setEmailBody(''); }}
                        data-testid={`email-sub-${sub.id}`}>
                        <Mail className="h-4 w-4 mr-1" /> Send Email
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Subscription Dialog */}
      <Dialog open={!!editSub} onOpenChange={() => setEditSub(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Subscription</DialogTitle>
            <DialogDescription>
              {editSub?.id} — {editSub?.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-bold">Items</Label>
                <Button size="sm" variant="outline" onClick={handleAddItem} data-testid="edit-add-item-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {editForm.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg" data-testid={`edit-item-${idx}`}>
                    <div className="flex-1">
                      <Input placeholder="Size (e.g. 20x25x1)" value={item.size}
                        onChange={(e) => handleEditItem(idx, 'size', e.target.value)}
                        className="h-8 text-sm" data-testid={`edit-item-size-${idx}`} />
                    </div>
                    <div className="w-20">
                      <Input type="number" placeholder="MERV" value={item.merv}
                        onChange={(e) => handleEditItem(idx, 'merv', e.target.value)}
                        className="h-8 text-sm" data-testid={`edit-item-merv-${idx}`} />
                    </div>
                    <div className="w-16">
                      <Input type="number" min="1" placeholder="Qty" value={item.quantity}
                        onChange={(e) => handleEditItem(idx, 'quantity', e.target.value)}
                        className="h-8 text-sm" data-testid={`edit-item-qty-${idx}`} />
                    </div>
                    <div className="w-20">
                      <Input type="number" step="0.01" placeholder="Price" value={item.price}
                        onChange={(e) => handleEditItem(idx, 'price', e.target.value)}
                        className="h-8 text-sm" data-testid={`edit-item-price-${idx}`} />
                    </div>
                    <button onClick={() => handleRemoveItem(idx)}
                      className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600"
                      data-testid={`edit-remove-item-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {editForm.items.length === 0 && (
                  <p className="text-sm text-red-500 bg-red-50 p-2 rounded">No items — add at least one item.</p>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="space-y-1.5">
              <Label className="text-sm font-bold">Shipping Address</Label>
              <Input value={editForm.shipping_address}
                onChange={(e) => setEditForm(prev => ({ ...prev, shipping_address: e.target.value }))}
                placeholder="Full shipping address"
                data-testid="edit-shipping-address" />
            </div>

            {/* Recurrence & Next Delivery */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-bold">Recurrence</Label>
                <Select value={editForm.recurrence_months}
                  onValueChange={(v) => setEditForm(prev => ({ ...prev, recurrence_months: v }))}>
                  <SelectTrigger data-testid="edit-recurrence"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">Every 2 months</SelectItem>
                    <SelectItem value="4">Every 4 months</SelectItem>
                    <SelectItem value="6">Every 6 months</SelectItem>
                    <SelectItem value="8">Every 8 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-bold">Next Delivery Date</Label>
                <Input type="date" value={editForm.next_delivery_date}
                  onChange={(e) => setEditForm(prev => ({ ...prev, next_delivery_date: e.target.value }))}
                  data-testid="edit-next-delivery" />
              </div>
            </div>

            {/* Discount */}
            <div className="space-y-1.5">
              <Label className="text-sm font-bold">Discount %</Label>
              <Input type="number" step="0.5" min="0" max="100" value={editForm.discount_percent}
                onChange={(e) => setEditForm(prev => ({ ...prev, discount_percent: e.target.value }))}
                data-testid="edit-discount" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditSub(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={editLoading}
              className="bg-gradient-to-r from-sky-500 to-teal-500"
              data-testid="edit-save-btn">
              {editLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEmailModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="email-modal">
            <div className="p-5 border-b">
              <h3 className="text-lg font-bold text-gray-900">Send Email to Customer</h3>
              <p className="text-sm text-gray-500">Subscription: {emailModal.slice(0, 12)}...</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Subject</label>
                <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                  placeholder="e.g. Your subscription update" data-testid="email-subject-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Message</label>
                <Textarea value={emailBody} onChange={e => setEmailBody(e.target.value)}
                  placeholder="Type your message here..." rows={6} data-testid="email-body-input" />
              </div>
            </div>
            <div className="p-5 border-t bg-slate-50 rounded-b-xl flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmailModal(null)} data-testid="email-cancel-btn">Cancel</Button>
              <Button onClick={handleSendEmail} disabled={sending} data-testid="email-send-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                Send Email
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminSubscriptions;
